<script lang="ts">
export default {
  name: 'KkTextField',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export interface Props {
  modelValue: string
  id?: string
  type?: string
  disabled?: boolean
  width?: string
  maxlength?: number
  placeholder?: string
  autocomplete?: string
  loading?: boolean
}

export interface Emits {
  (key: 'update:modelValue', value: Props['modelValue']): void
  (key: 'blur', value: FocusEvent): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  width: '100%',
})
const emits = defineEmits<Emits>()
const { t } = useI18n()

const valueModel = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value),
})

const placeholder = computed(() => props.placeholder || t('labels.placeholder.basic'))

const styles = computed(() => ({ width: props.width }))
</script>

<template>
  <div class="container" :style="styles">
    <input
      :id="id"
      v-model="valueModel"
      :type="props.type"
      :disabled="props.disabled"
      :placeholder="placeholder"
      :autocomplete="props.autocomplete"
      class="input"
      :maxlength="props.maxlength"
      @blur="emits('blur', $event)"
    >
    <div v-if="props.loading" class="loading">
      <svg class="loading__spinner" width="24px" height="24px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="loading__circle" fill="none" stroke="#E9446A" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30" />
      </svg>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/var";
.container {
  position: relative;
  border: 1px solid $color-base-c;
  border-radius: 4px;
}
.input {
  display: block;
  width: 100%;
  padding: 12px 16px;
  background-color: $color-base-a;
  font-weight: normal;
  font-size: 14px;
  line-height: 1.5;
  &::placeholder {
    color: rgba($color-font-base, .55);
  }
  &:disabled {
    background-color: #E2E2E2;
  }
}
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: calc(100% - 2px);
  background-color: #FFF;
  &__spinner {
    animation: rotation 1.35s linear infinite;
  }
  &__circle {
    stroke-dasharray: 180;
    stroke-dashoffset: 0;
    transform-origin: center;
    animation: turn 1.35s ease-in-out infinite;
  }
}
@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes turn {
  0% {
    stroke-dashoffset: 180;
    transform: rotate(90deg);
  }
  50% {
    stroke-dashoffset: 45;
    transform: rotate(135deg);
  }
  100% {
    stroke-dashoffset: 180;
    transform: rotate(450deg);
  }
}
</style>
